/**
 * Sheet space to screen space, and nothing else (`KLQ-20260820-001` phase 4).
 *
 * ADR-0006 left exactly one decision to implementation time — whether the pin overlay is
 * shared between the two viewers or written twice — and the spec resolved it: **shared**. So
 * the overlay takes normalised `0..1` coordinates plus an injected transform and knows nothing
 * about OpenSeadragon. This file is the one place allowed to, and `openSeadragonTransform` is
 * its only implementation today; a PDF.js path would supply a second one and reuse everything
 * above it.
 *
 * **Normalised in, CSS pixels out, and never a stored pixel anywhere.** A pin is a fraction of
 * the sheet because the pyramid's DPI is a rendering setting that has already been halved once
 * (ADR-0006), and an anchor kept in pixels would move the day it changes again. Every mapping
 * here runs normalised → the tiled image's *own* content size → viewport → element pixels, so
 * a rendition rebuilt at a different DPI, or a window dragged onto a display with a different
 * device pixel ratio, changes the numbers on both sides of the ratio and leaves the pin where
 * the person put it.
 *
 * Retro rule 8: pins, markup and later the compare panes all need this mapping, and deriving
 * it three times is how three of them drift. It lands once, here.
 *
 * **The seam grew two members on 2026-09-15 (issue #70, the markup layer), and both are the
 * inverse direction.** Until then the only gesture the sheet understood was a *press* — a pin
 * is a point, and `subscribePress` answered it by filtering drags out. Every markup gesture is
 * the thing that filter discards: a stroke is a drag, and a drag that draws is a drag that must
 * not also pan. So the interface now carries
 *
 *   - `toSheet(point)` — the inverse of `toScreen`, said out loud rather than re-derived by a
 *     caller. `subscribePress` and `subscribeDrag` are both written in terms of it below, which
 *     is what stops the arithmetic existing in three places (retro rule 8 again), and
 *   - `subscribeDrag(listener)` — a drag reported in sheet space, with the viewer's own pan
 *     suppressed for as long as somebody is listening. The suppression lives here rather than
 *     in the overlay for the same reason the press filter does: whether a gesture is a pan is
 *     the renderer's knowledge, and an overlay that took the pointer events itself to find out
 *     would be an overlay that can break panning by existing.
 *
 * Nothing above the seam changed: `PinLayer` and `ViewerCanvas` compile and behave exactly as
 * they did, and a future PDF.js transform owes these two the same as it owes the other four.
 */

import type {
  CanvasClickEvent,
  CanvasDragEvent,
  CanvasPressEvent,
  CanvasReleaseEvent,
  Viewer,
} from 'openseadragon';

/** A point on the sheet, normalised `0..1` in both axes — a `CoordinateAnchor` without a page. */
export interface SheetPoint {
  readonly x: number;
  readonly y: number;
}

/** A point on the sheet *and* the page it is on. What a pin's `anchor_ref` actually carries. */
export interface SheetAnchor extends SheetPoint {
  readonly page: number;
}

/** Where that point is right now, in CSS pixels relative to the overlay's own box. */
export interface ScreenPoint {
  readonly left: number;
  readonly top: number;
}

/**
 * One report from a drag across the sheet: which part of the gesture this is, and where.
 *
 * Three phases rather than a start/end pair, because a stroke is drawn *while* it happens —
 * the person watches the line follow the finger, and a gesture that only spoke at its ends
 * would be a stroke that appears after it is over.
 */
export interface SheetDrag {
  readonly phase: 'start' | 'move' | 'end';
  /** Clamped to `0..1`: a stroke stops at the edge of the sheet rather than leaving it. */
  readonly point: SheetPoint;
}

export interface SheetTransform {
  /** The page this transform maps. An anchor on another page is not on this sheet at all. */
  readonly page: number;
  /** `null` while the renderer has nothing open — a mapping that does not exist yet, said out loud. */
  toScreen(point: SheetPoint): ScreenPoint | null;
  /**
   * Back again: a pixel of the overlay's box, in sheet space. `null` for the same one reason
   * `toScreen` is — nothing is open, so there is no mapping either way.
   *
   * **It does not judge.** A pixel on the ink-coloured margin inverts to a coordinate outside
   * `0..1` and is returned as one; dropping it or clamping it here would make the seam decide
   * something each caller decides differently. `subscribePress` drops it (a pin has nowhere to
   * go), `subscribeDrag` clamps it (a stroke stops at the edge), and a hit test would want the
   * raw number.
   */
  toSheet(point: ScreenPoint): SheetPoint | null;
  /** Fly the viewport to that point. The only motion this seam is allowed to cause. */
  centreOn(point: SheetPoint): void;
  /** Fires whenever the mapping moved — pan, zoom, resize. Returns its own unsubscribe. */
  subscribe(listener: () => void): () => void;
  /**
   * A deliberate press on the sheet, reported in sheet space. Returns its own unsubscribe.
   *
   * The inverse direction of `toScreen`, and it belongs here for the same reason that one
   * does: placing a pin is *"where on the drawing did the person press"*, which is a question
   * about the sheet and not about the renderer. A caller that read a `MouseEvent` off the
   * overlay instead would have to know how the sheet is drawn to answer it — and would have to
   * decide, itself, whether a press that moved 200px was a placement or a pan.
   *
   * **A drag is not a press.** Panning an A0 with the mouse down is the gesture the viewer is
   * built around, and it must not leave a pin behind wherever the finger lifted.
   *
   * A press outside the sheet — on the ink-coloured margin around it — is not a point on the
   * drawing, and no listener is called for it. There is nowhere honest to anchor it.
   */
  subscribePress(listener: (point: SheetPoint) => void): () => void;
  /**
   * A drag across the sheet, reported in sheet space. Returns its own unsubscribe.
   *
   * **Subscribing is what turns a pan into a stroke.** For as long as a listener is attached,
   * a drag across the canvas draws instead of moving the sheet, and the momentum the viewer
   * would coast on afterwards is suppressed with it. Unsubscribing gives panning back
   * untouched — so "a tool is armed" and "somebody is listening to drags" are the same fact,
   * held in one place, and there is no second flag for them to disagree with.
   *
   * That is why this is not `subscribePress`'s twin with the filter removed. The press filter
   * asks *"was this a pan?"*; here the answer is decided rather than discovered, and only the
   * renderer can decide it — an overlay that took the pointer events itself to find out would
   * have to become opaque to them, and an opaque overlay is one bug away from a sheet that
   * cannot be panned at all.
   *
   * A drag that *starts* off the sheet is not reported at all: there is nowhere honest to
   * anchor its first point, which is the same answer `subscribePress` gives. Once it has
   * started, points outside the sheet are clamped to its edge rather than dropped — a finger
   * that runs past the margin and comes back drew one stroke, not two.
   *
   * **A press is still a press, and the caller owns that.** This suppresses the drag and its
   * flick; it does not touch `canvas-click`, so `subscribePress` keeps firing on a tap while a
   * tool is armed — which in Comment mode means the tap that was meant to start a stroke also
   * drops a pin. Arming a tool and subscribing to presses are two separate decisions made by
   * the same screen, so the screen makes them together: whoever arms a tool unsubscribes
   * `subscribePress` (or gates its listener) for as long as `tool !== null`. Deciding it here
   * would mean this seam quietly turning off a subscription somebody else asked for.
   */
  subscribeDrag(listener: (drag: SheetDrag) => void): () => void;
}

/**
 * A pin's anchor, read **through** `anchor_type` rather than around it.
 *
 * `MCK-20260521-006` keeps the anchor as `{ anchor_type, anchor_ref }` so that `element`
 * anchors can arrive later without a second shape for the same idea. A client that reached
 * into `anchor_ref` regardless would draw the day-one `element` pin at the sheet's top-left
 * corner and call it a coordinate. `null` here means *this screen cannot place it*, which is
 * the honest answer and is what the layer renders as nothing at all.
 *
 * `page` defaults to 0 rather than refusing: it was added to `CoordinateAnchor` on 2026-08-20
 * and a pin written before that is a single-page sheet's pin, which is page 0.
 */
export function sheetAnchor(pin: {
  readonly anchor_type: string;
  readonly anchor_ref: Record<string, unknown>;
}): SheetAnchor | null {
  if (pin.anchor_type !== 'coordinate') return null;
  const { x, y, page } = pin.anchor_ref;
  if (typeof x !== 'number' || typeof y !== 'number') return null;
  return { x, y, page: typeof page === 'number' ? page : 0 };
}

/**
 * The OpenSeadragon implementation — the only file in the feature that names the renderer.
 *
 * The content size is read from the tiled image rather than from the rendition row, because it
 * is the frame the viewport arithmetic is expressed in and reading it anywhere else would be
 * two sources for one number. `pixelFromPoint(…, true)` asks for the *current* animation frame
 * rather than the settled target, which is what makes a marker travel with the sheet during a
 * flick instead of jumping to where it will land.
 */
export function openSeadragonTransform(viewer: Viewer, page: number): SheetTransform {
  const viewportPointOf = (point: SheetPoint) => {
    // An empty world means nothing is open — between a page swap and its `open`, or after a
    // teardown that has not reached this closure yet.
    if (viewer.world.getItemCount() === 0) return null;
    const item = viewer.world.getItemAt(0);
    const size = item.getContentSize();
    return item.imageToViewportCoordinates(point.x * size.x, point.y * size.y, true);
  };

  /**
   * The same arithmetic run backwards, and the only implementation of it in this file.
   *
   * Takes loose numbers rather than a `ScreenPoint` because both event handlers below already
   * hold the pixel as two fields of an OpenSeadragon `Point`, and re-boxing it to unbox it
   * again would be ceremony.
   */
  const sheetPointOf = (left: number, top: number): SheetPoint | null => {
    if (viewer.world.getItemCount() === 0) return null;
    const item = viewer.world.getItemAt(0);
    const size = item.getContentSize();
    // A `Point` of the library's own class, minted from one it just handed us rather than with
    // `new OpenSeadragon.Point(...)`. This module is imported by the feature's barrel, and a
    // *value* import of OpenSeadragon here would pull ~350 kB into every chunk that touches
    // the barrel — `ViewerCanvas` is lazily imported precisely so that it does not.
    const pixel = size.clone();
    pixel.x = left;
    pixel.y = top;
    const image = item.viewportToImageCoordinates(
      viewer.viewport.pointFromPixel(pixel, true),
      true,
    );
    return { x: image.x / size.x, y: image.y / size.y };
  };

  /** `0..1` on both axes, because that is the frame every stored coordinate is written in. */
  const clampToSheet = (point: SheetPoint): SheetPoint => ({
    x: Math.min(1, Math.max(0, point.x)),
    y: Math.min(1, Math.max(0, point.y)),
  });

  /** On the drawing, rather than on the ink-coloured margin around it. */
  const onSheet = (point: SheetPoint): boolean =>
    point.x >= 0 && point.x <= 1 && point.y >= 0 && point.y <= 1;

  return {
    page,
    toScreen(point) {
      const viewportPoint = viewportPointOf(point);
      if (viewportPoint === null) return null;
      const pixel = viewer.viewport.pixelFromPoint(viewportPoint, true);
      return { left: pixel.x, top: pixel.y };
    },
    toSheet(point) {
      return sheetPointOf(point.left, point.top);
    },
    centreOn(point) {
      const viewportPoint = viewportPointOf(point);
      if (viewportPoint === null) return;
      viewer.viewport.panTo(viewportPoint);
      // Without this a pin near the edge of a zoomed-out sheet pans past `visibilityRatio`
      // and springs back on the next interaction, which reads as the click not having worked.
      viewer.viewport.applyConstraints();
    },
    subscribePress(listener) {
      // `canvas-click` rather than a DOM `click`: OpenSeadragon's own tracker is the only
      // thing that knows whether the pointer travelled, and `quick` is that knowledge — false
      // for the pan the viewer exists to make cheap, true for the tap that means something.
      const press = (event: CanvasClickEvent) => {
        if (!event.quick) return;
        const point = sheetPointOf(event.position.x, event.position.y);
        if (point === null) return;
        // Off the sheet. `CoordinateAnchor` is `0..1` on both axes and the server refuses
        // anything else, so a press on the margin is dropped here rather than sent to be
        // refused — the person pressed the background, which is not a refusal to report.
        if (!onSheet(point)) return;
        listener(point);
      };
      viewer.addHandler('canvas-click', press);
      return () => {
        viewer.removeHandler('canvas-click', press);
      };
    },
    subscribeDrag(listener) {
      // The gesture's own state, and the reason it is a closure rather than a field: two
      // subscriptions are two independent drags, and neither may end the other's.
      let drawing = false;
      /**
       * Whether the flick after this gesture still has to be suppressed.
       *
       * **`canvas-release` is raised BEFORE `canvas-drag-end`**, both from the same block of
       * `MouseTracker`'s capture handler — vendored OpenSeadragon 5.0.1,
       * `build/openseadragon/openseadragon.js`: `tracker.releaseHandler(…)` at ~6978, then
       * `tracker.dragEndHandler(…)` at ~6995, gated on `delegate.sentDragEvent`. So the
       * release cannot be what clears the gesture: a `flick` handler testing `drawing` would
       * find it already false, skip `preventDefaultAction`, and let the momentum branch
       * (~11149) run. `ViewerCanvas` enables `flickEnabled` for touch, so on a tablet any
       * stroke finished at ≥120 px/s would send the sheet coasting away from the line that
       * had just landed. The flag is set by the release and consumed by the drag-end.
       */
      let coasting = false;
      // Where the finger was last seen, in sheet space. The release below inverts its own
      // pixel, but a release that lands after the world emptied has nothing to invert — and a
      // gesture that never reports its end is a stroke that never finishes.
      let last: SheetPoint = { x: 0, y: 0 };

      const start = (event: CanvasPressEvent) => {
        const point = sheetPointOf(event.position.x, event.position.y);
        if (point === null || !onSheet(point)) return;
        drawing = true;
        // A gesture whose drag-end never arrived — a lost pointer capture — must not leave its
        // suppression armed for the next one.
        coasting = false;
        last = point;
        listener({ phase: 'start', point });
      };

      const move = (event: CanvasDragEvent) => {
        if (!drawing) return;
        // The pan this drag would otherwise have been. Set on every move rather than once at
        // the start, because OpenSeadragon reads it off each event it raises.
        event.preventDefaultAction = true;
        const point = sheetPointOf(event.position.x, event.position.y);
        if (point === null) return;
        last = clampToSheet(point);
        listener({ phase: 'move', point: last });
      };

      // `canvas-drag-end` carries the flick the viewer would coast on. Suppressed for the same
      // reason as the pan: the finger was drawing, and a sheet that glides away from a
      // finished stroke reads as the stroke having moved. It tests `coasting` rather than
      // `drawing` because it arrives *after* the release — see the flag's own note above.
      const flick = (event: CanvasDragEvent) => {
        if (!drawing && !coasting) return;
        coasting = false;
        event.preventDefaultAction = true;
      };

      const end = (event: CanvasReleaseEvent) => {
        if (!drawing) return;
        drawing = false;
        // Handed to the `canvas-drag-end` that OpenSeadragon raises immediately after this one.
        coasting = true;
        const point = sheetPointOf(event.position.x, event.position.y);
        if (point !== null) last = clampToSheet(point);
        listener({ phase: 'end', point: last });
      };

      viewer.addHandler('canvas-press', start);
      viewer.addHandler('canvas-drag', move);
      viewer.addHandler('canvas-drag-end', flick);
      viewer.addHandler('canvas-release', end);
      return () => {
        viewer.removeHandler('canvas-press', start);
        viewer.removeHandler('canvas-drag', move);
        viewer.removeHandler('canvas-drag-end', flick);
        viewer.removeHandler('canvas-release', end);
      };
    },
    subscribe(listener) {
      // `animation` is every frame the viewport moves, `after-resize` is the container having
      // settled at a new size. Between them there is no moment where the sheet has moved and
      // the markers have not.
      viewer.addHandler('animation', listener);
      viewer.addHandler('after-resize', listener);
      return () => {
        viewer.removeHandler('animation', listener);
        viewer.removeHandler('after-resize', listener);
      };
    },
  };
}
